// REACT
import { useState, useEffect } from "react";

// STYLED COMPONENTS
import {
  CarouselHeaderSubtext,
  CarouselContentAndStepper,
  CarouselWrapper,
  CarouselContent,
  CarouselStepper,
  CarouselBrokerImage,
  CarouselHeader,
  CarouselHeaderText,
  CarouselHeaderSubtextRedRectangle,
  CarouselHeaderSubtextText,
  CarouselStepperCircle,
  CarouselStepperRectangle,
  CarouselBrokerImageMobile,
  CarouselBrokerBodyMobile,
  CarouselContentAndStepperMobile,
  CarouselHeaderSubtextRedRectangleMobile,
  CarouselHeaderSubtextMobile,
} from "../styles/LandingPageCarousel.styles";

// CONSTANTS
import {
  CAROUSEL_TEXT,
  CAROUSEL_SUBHEADER_TEXT,
  getCarouselImages,
} from "../constants/carousel-images";

// HOOKS
import useIsUserFromUk from "src/utils/hooks/useIsUserFromUk";

interface LandingPageCarouselProps {
  isMobile: boolean;
  mode: "login" | "signup";
}

export function LandingPageCarousel({ isMobile, mode }: LandingPageCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const isUserFromUk = useIsUserFromUk();
  const images = getCarouselImages(mode, isMobile, isUserFromUk);
  const totalSlides = images.length;
  let headerText = CAROUSEL_TEXT;
  let subHeaderText = CAROUSEL_SUBHEADER_TEXT;
  if (isMobile) {
    headerText = headerText.slice(1);
    subHeaderText = subHeaderText.slice(1);
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50); // Small delay to ensure smooth transition
      }, 50);
    }, 6000);

    return () => clearInterval(timer);
  }, [currentSlide]); // Add currentSlide as dependency to reset timer when slide changes

  const handleManualSlideChange = (index: number) => {
    if (index !== currentSlide && !isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 50);
    }
  };

  // ---------------------- DESIGN ----------------------
  function CarouselHeaderComponent() {
    const HeaderSubtext = () => {
      return isMobile ? (
        <CarouselHeaderSubtextMobile>
          <CarouselHeaderSubtextRedRectangleMobile />
          <CarouselHeaderSubtextText>{subHeaderText[currentSlide]}</CarouselHeaderSubtextText>
        </CarouselHeaderSubtextMobile>
      ) : (
        <CarouselHeaderSubtext>
          <CarouselHeaderSubtextRedRectangle />
          <CarouselHeaderSubtextText>{subHeaderText[currentSlide]}</CarouselHeaderSubtextText>
        </CarouselHeaderSubtext>
      );
    };

    return (
      <CarouselHeader>
        <CarouselHeaderText>{headerText[currentSlide]}</CarouselHeaderText>
        <HeaderSubtext />
      </CarouselHeader>
    );
  }

  function CarouselBodyComponent() {
    return isMobile ? (
      <CarouselBrokerBodyMobile>
        <CarouselBrokerImageMobile
          key={`mobile-${currentSlide}`}
          src={images[currentSlide]}
          alt={headerText[currentSlide]}
        />
      </CarouselBrokerBodyMobile>
    ) : (
      <CarouselBrokerImage backgroundImage={images[currentSlide]} />
    );
  }

  function CarouselContentComponent() {
    return (
      <CarouselContent>
        {!!headerText[currentSlide] && <CarouselHeaderComponent />}
        <CarouselBodyComponent />
      </CarouselContent>
    );
  }

  function CarouselStepperComponent() {
    return (
      <CarouselStepper>
        {images.map((_, index) =>
          index === currentSlide ? (
            <CarouselStepperRectangle
              key={index}
              onClick={() => handleManualSlideChange(index)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <CarouselStepperCircle
              key={index}
              onClick={() => handleManualSlideChange(index)}
              style={{ cursor: "pointer" }}
            />
          ),
        )}
      </CarouselStepper>
    );
  }

  return isMobile ? (
    <CarouselContentAndStepperMobile>
      <CarouselHeaderComponent />
      <CarouselBodyComponent />
      <CarouselStepperComponent />
    </CarouselContentAndStepperMobile>
  ) : (
    <CarouselWrapper item xs={12} md={6} lg={6}>
      <CarouselContentAndStepper>
        <CarouselContentComponent />
        <CarouselStepperComponent />
      </CarouselContentAndStepper>
    </CarouselWrapper>
  );
}
